import * as React from "react";
import { Slider as SliderPrimitive } from "radix-ui";

import { cn } from "../lib/utils";

/**
 * shadcn `slider` primitive (new-york), adapted to the unified `radix-ui` import
 * convention. Atlas-styled: `bg-muted` track, accent (`bg-primary`) range, and a
 * hairline-bordered thumb with the standard focus ring. Thumbs are derived from
 * `value`/`defaultValue` so multi-thumb ranges render correctly.
 *
 * `aria-label` is forwarded to every thumb (the role="slider" element) so a
 * single-thumb slider is axe-labelled without an external <label> — a span thumb
 * can not be associated via htmlFor. ponytail: one shared label across thumbs;
 * pass distinct per-thumb labels via `aria-label` on a custom render if a
 * multi-thumb slider ever needs them.
 */
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  "aria-label": ariaLabel,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root> & {
  "aria-label"?: string;
}) {
  const values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max],
  );

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className="bg-muted relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className="bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
        />
      </SliderPrimitive.Track>
      {Array.from({ length: values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          key={index}
          aria-label={ariaLabel}
          data-slot="slider-thumb"
          className="border-primary bg-background ring-ring/50 focus-visible:ring-4 block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] duration-150 motion-reduce:transition-none outline-none hover:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
